import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const LangContext = createContext(null);
const STORAGE_KEY = 'hca_ui_lang';

// Translation dictionary. Keys use dot notation. Default language within the app is Spanish.
// Landing page default is German (set on a per-page basis via <LangProvider defaultLang="de">).
const translations = {
  es: {
    // Navigation
    'nav.practice': 'Practicar',
    'nav.aiTeacher': 'Prof. IA',
    'nav.buddies': 'Amigos',
    'nav.course': 'Curso',
    'nav.profile': 'Perfil',
    'nav.teacher': 'Profesor',
    'nav.classes': 'Clases',
    'nav.materials': 'Material',
    'nav.analysis': 'Análisis',
    'nav.album': 'Álbum',
    // Album page
    'album.header.pre': 'Tu Álbum',
    'album.header.title': 'Colecciona los tesoros de España',
    'album.header.sub': 'Cada nivel desbloquea una personalidad, un lugar o una canción de España. Toca las tarjetas desbloqueadas para leer más.',
    'album.tab.personalities': 'Personalidades',
    'album.tab.cities': 'Lugares',
    'album.tab.songs': 'Canciones',
    'album.tab.all': 'Todos',
    'album.progress.unlocked': '{label} desbloqueados',
    'album.locked': '???',
    'album.locked.modal': 'Alcanza {n} puntos para desbloquear este {type}.',
    'album.type.personality': 'personaje',
    'album.type.city': 'lugar',
    'album.type.song': 'canción',
    'modal.section.bio.personality': 'Biografía',
    'modal.section.bio.city': 'La provincia',
    'modal.section.bio.song': 'La canción',
    'modal.section.fact': '¿Sabías que?',
    'modal.required': 'Puntos necesarios',
    'modal.level': 'Nivel',
    // Language toggle
    'lang.switchTo': 'Cambiar a',
    'lang.label.es': 'Español',
    'lang.label.de': 'Deutsch',
    // Navigation (main app)
    'nav.dashboard': 'Inicio',
    'nav.community': 'Comunidad',
    'nav.dudas': 'Dudas',
    'nav.exercises': 'Ejercicios',
    'nav.games': 'Juegos',
    'nav.challenges': 'Desafío',
    'nav.profile2': 'Perfil',
    'nav.logout': 'Cerrar sesión',
    'nav.notifications': 'Notificaciones',
    // Generic
    'btn.save': 'Guardar',
    'btn.cancel': 'Cancelar',
    'btn.submit': 'Enviar',
    'btn.delete': 'Eliminar',
    'btn.edit': 'Editar',
    'btn.close': 'Cerrar',
    'btn.loading': 'Cargando...',
    'btn.retry': 'Reintentar',
    'btn.back': 'Atrás',
    // Page titles
    'page.materials_title': 'Material didáctico',
    'page.exercises_title': 'Ejercicios',
    'page.community_title': 'Comunidad',
    'page.dudas_title': 'Dudas',
    'page.games_title': 'Juegos',
    'page.challenges_title': 'Desafío semanal',
    // Exercises
    'ex.type_picker_title': '¿Qué tipo de ejercicio quieres practicar?',
    'ex.type_picker_subtitle': 'Elige un tipo para cargar solo los ejercicios correspondientes (más rápido).',
    'ex.change_type': 'Cambiar tipo',
    'ex.load_more': 'Cargar más',
    'ex.none_found': 'No hay ejercicios',
    'ex.last_correct': 'Último intento: correcto',
    'ex.last_incorrect': 'Último intento: incorrecto',
    // Materials
    'mat.subtitle_student': 'Material exclusivo para tu clase',
    'mat.not_assigned': 'Aún no estás asignado a ninguna clase. Contacta con tu profesor.',
    'mat.entry': 'entrada',
    'mat.entries': 'entradas',
    'mat.no_material_yet': 'Tu profesor subirá material pronto.',
  },
  de: {
    'nav.practice': 'Üben',
    'nav.aiTeacher': 'KI-Lehrerin',
    'nav.buddies': 'Buddies',
    'nav.course': 'Kurs',
    'nav.profile': 'Profil',
    'nav.teacher': 'Lehrer',
    'nav.classes': 'Kurse',
    'nav.materials': 'Material',
    'nav.analysis': 'Analyse',
    'nav.album': 'Album',
    'album.header.pre': 'Dein Album',
    'album.header.title': 'Sammle Spaniens Schätze',
    'album.header.sub': 'Jedes Level schaltet eine Persönlichkeit, einen Ort oder ein Lied aus Spanien frei. Tippe auf freigeschaltete Einträge, um mehr zu erfahren.',
    'album.tab.personalities': 'Persönlichkeiten',
    'album.tab.cities': 'Orte',
    'album.tab.songs': 'Lieder',
    'album.tab.all': 'Alle',
    'album.progress.unlocked': '{label} freigeschaltet',
    'album.locked': '???',
    'album.locked.modal': 'Erreiche {n} Punkte, um diesen {type} freizuschalten.',
    'album.type.personality': 'Eintrag',
    'album.type.city': 'Ort',
    'album.type.song': 'Lied',
    'modal.section.bio.personality': 'Biografie',
    'modal.section.bio.city': 'Die Provinz',
    'modal.section.bio.song': 'Das Lied',
    'modal.section.fact': 'Wusstest du?',
    'modal.required': 'Benötigte Punkte',
    'modal.level': 'Level',
    'lang.switchTo': 'Wechseln zu',
    'lang.label.es': 'Español',
    'lang.label.de': 'Deutsch',
    // Navigation
    'nav.dashboard': 'Startseite',
    'nav.community': 'Community',
    'nav.dudas': 'Dudas',
    'nav.exercises': 'Übungen',
    'nav.games': 'Spiele',
    'nav.challenges': 'Challenge',
    'nav.profile2': 'Profil',
    'nav.logout': 'Abmelden',
    'nav.notifications': 'Benachrichtigungen',
    // Generic
    'btn.save': 'Speichern',
    'btn.cancel': 'Abbrechen',
    'btn.submit': 'Absenden',
    'btn.delete': 'Löschen',
    'btn.edit': 'Bearbeiten',
    'btn.close': 'Schließen',
    'btn.loading': 'Lädt...',
    'btn.retry': 'Erneut versuchen',
    'btn.back': 'Zurück',
    // Page titles
    'page.materials_title': 'Unterrichtsmaterial',
    'page.exercises_title': 'Übungen',
    'page.community_title': 'Community',
    'page.dudas_title': 'Dudas',
    'page.games_title': 'Spiele',
    'page.challenges_title': 'Wochen-Challenge',
    // Exercises
    'ex.type_picker_title': 'Welchen Übungstyp möchtest du üben?',
    'ex.type_picker_subtitle': 'Wähle einen Typ, damit wir nur passende Übungen laden (schneller).',
    'ex.change_type': 'Übungstyp ändern',
    'ex.load_more': 'Mehr laden',
    'ex.none_found': 'Keine Übungen gefunden',
    'ex.last_correct': 'Letzter Versuch: richtig',
    'ex.last_incorrect': 'Letzter Versuch: falsch',
    // Materials
    'mat.subtitle_student': 'Material exklusiv für deine Klasse',
    'mat.not_assigned': 'Du bist noch keiner Klasse zugewiesen. Wende dich an deinen Lehrer.',
    'mat.entry': 'Eintrag',
    'mat.entries': 'Einträge',
    'mat.no_material_yet': 'Dein Lehrer wird hier bald Materialien hochladen',
  },
};

export const LangProvider = ({ children, defaultLang = 'de' }) => {
  // useAuth is called unconditionally; if AuthContext isn't mounted (landing routes),
  // the context's default returns `{ user: null }` so this is always safe.
  const authUser = useAuth()?.user || null;

  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'es' || saved === 'de') return saved;
    } catch { /* ignore */ }
    return defaultLang;
  });

  // Auto-force Spanish for B1/B2/C1 students. Teachers/admins stay in German.
  // Saved user choice still takes precedence, so this only applies when there's
  // no explicit localStorage override.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return; // respect user choice
    } catch { /* ignore */ }
    if (authUser && !['admin', 'teacher'].includes(authUser.role) && ['B1', 'B2', 'C1'].includes(authUser.spanish_level)) {
      setLangState('es');
    } else {
      setLangState(defaultLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.spanish_level, authUser?.role, defaultLang]);

  const setLang = (l) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  };

  const t = (key, params = {}) => {
    const dict = translations[lang] || translations.es;
    let str = dict[key] ?? translations.es[key] ?? key;
    Object.entries(params).forEach(([k, v]) => {
      str = str.replaceAll(`{${k}}`, String(v));
    });
    return str;
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) return { lang: 'es', setLang: () => {}, t: (k) => k };
  return ctx;
};

/**
 * Pick between a German and Spanish value from a backend object.
 * Backend level/album records use `bio_de`/`bio_es`, `fact_de`/`fact_es`, etc.
 * Falls back to the other language if the preferred one is empty.
 */
export const pickLang = (obj, baseKey, lang) => {
  if (!obj) return null;
  const primary = obj[`${baseKey}_${lang}`];
  if (primary) return primary;
  const fallback = obj[`${baseKey}_${lang === 'es' ? 'de' : 'es'}`];
  return fallback || obj[baseKey] || null;
};
